import { Flame, ArrowRight, AlertTriangle } from "lucide-react";
import { imageForPack } from "@/lib/assets";
import { useOrder } from "@/context/CartContext";

const DESCRIPTIONS = {
  "nibs-10": "Personal feed. Ten crispy bites, max crunch.",
  "nibs-20": "Twenty bites of pure golden trouble. Share — or don't.",
  "nibs-30": "Three dozen crispy little legends for the whole crew.",
  "nibs-45": "Stacked, snackable, gone in 60 seconds. Maybe.",
  "nibs-55": "The Burn City Beast. Feed the whole block.",
};

const BADGES = {
  "nibs-30": { label: "Crowd Fave", className: "bg-[#ED0B09] text-[#F6F0E8]" },
  "nibs-55": { label: "Beast Mode", className: "bg-[#F4A623] text-[#171D4F]" },
};

export default function MenuSection() {
  const { openWith, inventory, loadingInventory } = useOrder();

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative py-20 md:py-28 bg-[#0A0D26]"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#ED0B09]/60 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#F4A623]">The Lineup</div>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-[#F6F0E8]">
              The <span className="text-[#ED0B09] text-glow-red">Nibs</span>
            </h2>
            <p className="mt-3 max-w-xl text-[#F6F0E8]/75 text-base md:text-lg">
              One flavour. Five pack sizes. Limited drop — first in, best dressed.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[#F6F0E8]/70 text-sm uppercase tracking-widest">
            <Flame size={16} className="text-[#ED0B09]" /> Cooked to order
          </div>
        </div>

        {inventory.all_sold_out && !loadingInventory && (
          <div
            data-testid="menu-sold-out-banner"
            className="mb-8 rounded-3xl border border-[#ED0B09]/40 bg-[#ED0B09]/10 p-5 flex items-center gap-3 text-[#F6F0E8]"
          >
            <AlertTriangle className="text-[#ED0B09]" />
            <div>
              <div className="font-display text-xl uppercase">This drop is sold out</div>
              <div className="text-sm text-[#F6F0E8]/75">Follow @gullwaynibs on Instagram — the next drop will be announced soon.</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {(inventory.items || []).map((item) => {
            const badge = BADGES[item.id];
            const soldOut = item.sold_out;
            const remaining = item.remaining;
            const low = !soldOut && remaining <= Math.max(2, Math.ceil(item.limit * 0.2));
            return (
              <article
                key={item.id}
                data-testid={`menu-item-${item.id}`}
                className={`group relative bg-[#171D4F]/60 backdrop-blur-md border rounded-3xl overflow-hidden transition-all duration-500 ${
                  soldOut
                    ? "border-[#F6F0E8]/10 opacity-70"
                    : "border-[#F6F0E8]/10 hover:border-[#ED0B09]/60 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(237,11,9,0.25)]"
                }`}
              >
                <div className="relative aspect-[5/4] overflow-hidden">
                  <img
                    src={imageForPack(item.id)}
                    alt={item.name}
                    loading="lazy"
                    className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${
                      soldOut ? "grayscale" : "group-hover:scale-110"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D26] via-[#0A0D26]/30 to-transparent" />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className="bg-[#171D4F]/80 backdrop-blur-md border border-[#F6F0E8]/15 text-[#F4A623] text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full">
                      {item.tag}
                    </span>
                    {badge && (
                      <span className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full ${badge.className}`}>
                        {badge.label}
                      </span>
                    )}
                  </div>
                  <div className="absolute top-3 right-3 bg-[#0A0D26]/80 backdrop-blur-md border border-[#F6F0E8]/15 px-3 py-1.5 rounded-full">
                    <span className="font-display text-xl text-[#F6F0E8] leading-none">${item.price}</span>
                  </div>
                  {soldOut && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="font-display text-3xl sm:text-4xl uppercase tracking-widest bg-[#0A0D26]/85 border border-[#ED0B09]/50 text-[#ED0B09] px-5 py-2 rounded-2xl">
                        Sold Out
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-5 sm:p-6">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-display text-2xl sm:text-3xl uppercase text-[#F6F0E8]">{item.name}</h3>
                    <span className="text-xs uppercase tracking-widest text-[#F6F0E8]/60">{item.size} nibs</span>
                  </div>
                  <p className="mt-2 text-sm text-[#F6F0E8]/70 leading-relaxed">{DESCRIPTIONS[item.id]}</p>

                  <div className="mt-4 flex items-center justify-between text-xs uppercase tracking-widest">
                    <span
                      data-testid={`menu-item-stock-${item.id}`}
                      className={`inline-flex items-center gap-1.5 ${
                        soldOut ? "text-[#ED0B09]" : low ? "text-[#F4A623]" : "text-[#F6F0E8]/55"
                      }`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${soldOut ? "bg-[#ED0B09]" : low ? "bg-[#F4A623]" : "bg-emerald-400"}`} />
                      {soldOut ? "Sold out" : `${remaining} left`}
                    </span>
                    {low && !soldOut && <span className="text-[#F4A623]">Low stock</span>}
                  </div>

                  <button
                    type="button"
                    onClick={() => !soldOut && openWith({ id: item.id, name: item.name, size: item.size, price: item.price, tag: item.tag, remaining })}
                    disabled={soldOut}
                    data-testid={`menu-item-order-${item.id}`}
                    className={`mt-5 w-full inline-flex items-center justify-center gap-2 font-bold uppercase tracking-[0.18em] px-5 py-3 rounded-full transition-all duration-300 ${
                      soldOut
                        ? "bg-[#171D4F] text-[#F6F0E8]/45 cursor-not-allowed border border-[#F6F0E8]/10"
                        : "bg-[#ED0B09] hover:bg-[#B42A27] text-[#F6F0E8] shadow-[0_0_20px_rgba(237,11,9,0.35)] hover:shadow-[0_0_36px_rgba(237,11,9,0.75)]"
                    }`}
                  >
                    {soldOut ? "Sold Out" : (<>Pre-order This Pack <ArrowRight size={16} /></>)}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
